import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaCalendar, FaUser, FaClock, FaArrowLeft, FaShare, FaFacebook, FaTwitter, FaLinkedin, FaEye } from 'react-icons/fa';
import { fetchBlogById, fetchBlogs, clearCurrentBlog } from '../redux/slices/blogSlice';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
};

const toHtml = (text) => {
  if (!text) return '';
  if (/<[a-zA-Z][\s\S]*?>/.test(text)) return text;
  return '<p>' +
    text
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\n\n+/g, '</p><p>')
      .replace(/\n/g, '<br>')
    + '</p>';
};

const getReadTime = (content) => {
  if (!content) return '5 min read';
  return `${Math.max(1, Math.ceil(content.split(/\s+/).length / 200))} min read`;
};

const BlogDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentBlog: post, blogs, loading, error } = useSelector((state) => state.blog);

  useEffect(() => {
    dispatch(clearCurrentBlog());
    dispatch(fetchBlogById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (post?.category) {
      dispatch(fetchBlogs({ category: post.category, limit: 4 }));
    }
  }, [dispatch, post?.category]);

  const relatedPosts = blogs.filter((b) => b._id !== post?._id).slice(0, 3);

  const handleShare = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(post?.title || '');
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    window.open(links[platform], '_blank', 'width=600,height=400');
  };

  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (loading && !post) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center text-white">
          <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 mt-4">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error && !post) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={() => navigate('/blog')}
            className="bg-primary hover:bg-primary/90 px-6 py-3 rounded-xl font-semibold transition-colors"
          >
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          {post.featuredImage?.url ? (
            <img
              src={post.featuredImage.url}
              alt={post.title}
              className="w-full h-full object-cover opacity-30"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/20 to-gray-900" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 text-gray-300 hover:text-white mb-8 transition-colors"
          >
            <FaArrowLeft />
            <span>Back to Blog</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-block bg-primary/20 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6"
          >
            {post.category}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl md:text-6xl font-black mb-6 leading-tight"
          >
            {post.title}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-wrap items-center gap-6 text-gray-300"
          >
            <div className="flex items-center gap-2">
              <FaUser className="text-primary" />
              <span>{post.author?.fullName || 'MyFitness Team'}</span>
            </div>
            <div className="flex items-center gap-2">
              <FaCalendar className="text-primary" />
              <span>{formatDate(post.publishedAt || post.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <FaClock className="text-primary" />
              <span>{getReadTime(post.content)}</span>
            </div>
            {post.views > 0 && (
              <div className="flex items-center gap-2">
                <FaEye className="text-primary" />
                <span>{post.views.toLocaleString()} views</span>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass rounded-3xl p-8 md:p-12"
          >
            {post.featuredImage?.url && (
              <div className="mb-12 rounded-2xl overflow-hidden">
                <img
                  src={post.featuredImage.url}
                  alt={post.title}
                  className="w-full h-96 object-cover"
                />
              </div>
            )}

            {post.excerpt && (
              <p className="text-xl text-gray-300 italic border-l-4 border-primary pl-6 mb-10">
                {post.excerpt}
              </p>
            )}

            <div
              className="prose prose-invert prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: toHtml(post.content) }}
              style={{ color: '#e5e7eb' }}
            />

            {post.tags?.length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-white/10 text-gray-300 px-3 py-1 rounded-full text-sm"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Share */}
            <div className="mt-12 pt-8 border-t border-gray-700">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <h3 className="text-xl font-bold">Share this article</h3>
                <div className="flex gap-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="bg-blue-600 hover:bg-blue-700 p-3 rounded-full transition-colors"
                    aria-label="Share on Facebook"
                  >
                    <FaFacebook size={20} />
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="bg-sky-500 hover:bg-sky-600 p-3 rounded-full transition-colors"
                    aria-label="Share on Twitter"
                  >
                    <FaTwitter size={20} />
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="bg-blue-700 hover:bg-blue-800 p-3 rounded-full transition-colors"
                    aria-label="Share on LinkedIn"
                  >
                    <FaLinkedin size={20} />
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className="bg-gray-700 hover:bg-gray-600 p-3 rounded-full transition-colors"
                    aria-label="Copy link"
                  >
                    <FaShare size={20} />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost, index) => (
                <motion.div
                  key={relatedPost._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                  className="glass rounded-2xl overflow-hidden cursor-pointer group hover:scale-105 transition-transform"
                >
                  <div className="relative h-48 overflow-hidden">
                    {relatedPost.featuredImage?.url ? (
                      <img
                        src={relatedPost.featuredImage.url}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/30 to-gray-800 flex items-center justify-center">
                        <span className="text-4xl opacity-20">💪</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="inline-block bg-primary/20 text-primary px-3 py-1 rounded-full text-xs font-semibold mb-3">
                      {relatedPost.category}
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">{relatedPost.excerpt}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <div className="flex items-center gap-1">
                        <FaCalendar />
                        <span>{formatDate(relatedPost.publishedAt || relatedPost.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetailsPage;
